import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { CheckCircle2Icon, ChevronRightIcon, ChevronLeftIcon, FileTextIcon, UploadCloudIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function SkipTheLinePage() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    email: '',
    address: '',
    insuranceProvider: '',
    memberId: '',
    groupNumber: '',
    allergies: '',
    medications: '',
    conditions: [] as string[]
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConditionToggle = (condition: string) => {
    setFormData(prev => {
      if (condition === 'None') return {
        ...prev,
        conditions: ['None']
      };
      const newConditions = prev.conditions.filter(c => c !== 'None');
      if (newConditions.includes(condition)) {
        return {
          ...prev,
          conditions: newConditions.filter(c => c !== condition)
        };
      }
      return {
        ...prev,
        conditions: [...newConditions, condition]
      };
    });
  };
  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  if (isSuccess) {
    return <>
        <PageHero eyebrow="Skip the Line" title="Paperwork Complete" subtitle="You're all set for your visit." data-id="element-645" />
        <section className="py-20 bg-gray-50 min-h-[50vh] flex items-center justify-center" data-id="element-646">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white p-10 rounded-[10px] shadow-lg max-w-lg w-full text-center border border-[#E5E9F0]" data-id="element-647">
            <CheckCircle2Icon className="h-20 w-20 text-teal-500 mx-auto mb-6" data-id="element-648" />
            <h2 className="text-3xl font-bold text-navy-500 mb-4" data-id="element-649">Success!</h2>
            <p className="text-gray-600 mb-8 text-lg" data-id="element-650">
              Your digital paperwork has been securely submitted. Your
              confirmation number is{' '}
              <strong data-id="element-651">#HCF-{Math.floor(Math.random() * 90000) + 10000}</strong>.
            </p>
            <a href="/" className="inline-flex items-center justify-center bg-[#0A71B1] hover:bg-[#085d91] text-white px-8 py-3 rounded-[10px] font-semibold transition-colors" data-id="element-652">
              Return to Home
            </a>
          </motion.div>
        </section>
      </>;
  }
  return <>
      <PageHero eyebrow="Skip the Line" title="Complete Your Paperwork Before You Arrive" subtitle="Save time in the waiting room by securely submitting your forms online." bgImage="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&q=80" data-id="element-653" />

      <section className="py-16 md:py-24 bg-gray-50" data-id="element-654">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-655">
          {/* Progress Bar */}
          <div className="mb-12" data-id="element-656">
            <div className="flex justify-between mb-2" data-id="element-657">
              {['Personal Info', 'Insurance', 'Medical History', 'Review'].map((label, i) => <span key={label} className={`text-xs font-bold uppercase tracking-wider ${step >= i + 1 ? 'text-[#0A71B1]' : 'text-gray-400'}`} data-id="element-658">
                    {label}
                  </span>)}
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-659">
              <motion.div className="h-full bg-[#0A71B1]" initial={{
              width: '25%'
            }} animate={{
              width: `${step / 4 * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-660" />
            </div>
          </div>

          <motion.div className="bg-white rounded-[10px] shadow-sm border border-[#E5E9F0] p-6 md:p-10" variants={containerVariants} initial="hidden" animate="show" data-id="element-661">
            <form onSubmit={step === 4 ? handleSubmit : e => {
            e.preventDefault();
            nextStep();
          }} data-id="element-662">
              {step === 1 && <motion.div variants={itemVariants} className="space-y-6" data-id="element-663">
                  <h2 className="text-2xl font-bold text-navy-500 flex items-center gap-2" data-id="element-664">
                    <FileTextIcon className="h-6 w-6 text-[#0A71B1]" data-id="element-665" /> Personal
                    Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-666">
                    <div data-id="element-667">
                      <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-668">
                        First Name
                      </label>
                      <input required type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="Jane" data-id="element-669" />
                    </div>
                    <div data-id="element-670">
                      <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-671">
                        Last Name
                      </label>
                      <input required type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="Doe" data-id="element-672" />
                    </div>
                    <div data-id="element-673">
                      <label htmlFor="dob" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-674">
                        Date of Birth
                      </label>
                      <input required type="date" id="dob" name="dob" value={formData.dob} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" data-id="element-675" />
                    </div>
                    <div data-id="element-676">
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-677">
                        Phone Number
                      </label>
                      <input required type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="(555) 123-4567" data-id="element-678" />
                    </div>
                    <div className="md:col-span-2" data-id="element-679">
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-680">
                        Email Address
                      </label>
                      <input required type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="jane@example.com" data-id="element-681" />
                    </div>
                    <div className="md:col-span-2" data-id="element-682">
                      <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-683">
                        Home Address
                      </label>
                      <input required type="text" id="address" name="address" value={formData.address} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="123 Main St, Apt 4B, City, State, ZIP" data-id="element-684" />
                    </div>
                  </div>
                </motion.div>}

              {step === 2 && <motion.div variants={itemVariants} className="space-y-6" data-id="element-685">
                  <h2 className="text-2xl font-bold text-navy-500 flex items-center gap-2" data-id="element-686">
                    <FileTextIcon className="h-6 w-6 text-[#0A71B1]" data-id="element-687" />{' '}
                    Insurance Details
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-688">
                    <div className="md:col-span-2" data-id="element-689">
                      <label htmlFor="insuranceProvider" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-690">
                        Insurance Provider
                      </label>
                      <select required id="insuranceProvider" name="insuranceProvider" value={formData.insuranceProvider} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1] bg-white" data-id="element-691">
                        <option value="" data-id="element-692">Select Provider...</option>
                        <option value="BlueCross" data-id="element-693">BlueCross BlueShield</option>
                        <option value="Aetna" data-id="element-694">Aetna</option>
                        <option value="Cigna" data-id="element-695">Cigna</option>
                        <option value="UnitedHealthcare" data-id="element-696">
                          UnitedHealthcare
                        </option>
                        <option value="Medicare" data-id="element-697">Medicare / Medicaid</option>
                        <option value="SelfPay" data-id="element-698">Self-Pay / No Insurance</option>
                      </select>
                    </div>
                    {formData.insuranceProvider !== 'SelfPay' && <>
                        <div data-id="element-699">
                          <label htmlFor="memberId" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-700">
                            Member ID
                          </label>
                          <input type="text" id="memberId" name="memberId" value={formData.memberId} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="ABC123456789" data-id="element-701" />
                        </div>
                        <div data-id="element-702">
                          <label htmlFor="groupNumber" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-703">
                            Group Number
                          </label>
                          <input type="text" id="groupNumber" name="groupNumber" value={formData.groupNumber} onChange={handleInputChange} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="GRP98765" data-id="element-704" />
                        </div>
                        <div className="md:col-span-2" data-id="element-705">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-706">
                            Upload Insurance Card (Optional)
                          </label>
                          <div className="border-2 border-dashed border-[#E5E9F0] rounded-[10px] p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-707">
                            <UploadCloudIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" data-id="element-708" />
                            <p className="text-sm text-gray-500 font-medium" data-id="element-709">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-400 mt-1" data-id="element-710">
                              PNG, JPG up to 5MB
                            </p>
                          </div>
                        </div>
                      </>}
                  </div>
                </motion.div>}

              {step === 3 && <motion.div variants={itemVariants} className="space-y-6" data-id="element-711">
                  <h2 className="text-2xl font-bold text-navy-500 flex items-center gap-2" data-id="element-712">
                    <FileTextIcon className="h-6 w-6 text-[#0A71B1]" data-id="element-713" /> Medical
                    History
                  </h2>

                  <div data-id="element-714">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-715">
                      Do you have any of the following conditions? (Check all
                      that apply)
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-716">
                      {['Diabetes', 'Hypertension', 'Asthma', 'Heart Disease', 'Thyroid Disorder', 'None'].map(condition => <label key={condition} className="flex items-center gap-3 p-3 border border-[#E5E9F0] rounded-[10px] cursor-pointer hover:bg-gray-50" data-id="element-717">
                          <input type="checkbox" checked={formData.conditions.includes(condition)} onChange={() => handleConditionToggle(condition)} className="h-5 w-5 text-[#0A71B1] rounded border-gray-300 focus:ring-[#0A71B1]" data-id="element-718" />
                          <span className="text-gray-700" data-id="element-719">{condition}</span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-720">
                    <label htmlFor="allergies" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-721">
                      Allergies (Medications, Food, etc.)
                    </label>
                    <textarea id="allergies" name="allergies" value={formData.allergies} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="List any allergies or type 'None'..." data-id="element-722" />
                  </div>

                  <div data-id="element-723">
                    <label htmlFor="medications" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-724">
                      Current Medications & Dosages
                    </label>
                    <textarea id="medications" name="medications" value={formData.medications} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 rounded-[10px] border border-[#E5E9F0] focus:outline-none focus:ring-2 focus:ring-[#0A71B1]" placeholder="List current medications or type 'None'..." data-id="element-725" />
                  </div>
                </motion.div>}

              {step === 4 && <motion.div variants={itemVariants} className="space-y-8" data-id="element-726">
                  <h2 className="text-2xl font-bold text-navy-500 flex items-center gap-2" data-id="element-727">
                    <CheckCircle2Icon className="h-6 w-6 text-[#0A71B1]" data-id="element-728" />{' '}
                    Review & Submit
                  </h2>

                  <div className="bg-gray-50 rounded-[10px] p-6 border border-[#E5E9F0]" data-id="element-729">
                    <div className="flex justify-between items-center mb-4" data-id="element-730">
                      <h3 className="text-lg font-bold text-navy-500" data-id="element-731">
                        Personal Info
                      </h3>
                      <button type="button" onClick={() => setStep(1)} className="text-sm text-[#0A71B1] font-semibold hover:underline" data-id="element-732">
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-sm" data-id="element-733">
                      <div className="text-gray-500" data-id="element-734">Name:</div>
                      <div className="font-medium text-gray-900" data-id="element-735">
                        {formData.firstName} {formData.lastName}
                      </div>
                      <div className="text-gray-500" data-id="element-736">DOB:</div>
                      <div className="font-medium text-gray-900" data-id="element-737">
                        {formData.dob}
                      </div>
                      <div className="text-gray-500" data-id="element-738">Email:</div>
                      <div className="font-medium text-gray-900" data-id="element-739">
                        {formData.email}
                      </div>
                      <div className="text-gray-500" data-id="element-740">Phone:</div>
                      <div className="font-medium text-gray-900" data-id="element-741">
                        {formData.phone}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-[10px] p-6 border border-[#E5E9F0]" data-id="element-742">
                    <div className="flex justify-between items-center mb-4" data-id="element-743">
                      <h3 className="text-lg font-bold text-navy-500" data-id="element-744">
                        Insurance
                      </h3>
                      <button type="button" onClick={() => setStep(2)} className="text-sm text-[#0A71B1] font-semibold hover:underline" data-id="element-745">
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-sm" data-id="element-746">
                      <div className="text-gray-500" data-id="element-747">Provider:</div>
                      <div className="font-medium text-gray-900" data-id="element-748">
                        {formData.insuranceProvider || 'Not specified'}
                      </div>
                      <div className="text-gray-500" data-id="element-749">Member ID:</div>
                      <div className="font-medium text-gray-900" data-id="element-750">
                        {formData.memberId || 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-[10px] p-6 border border-[#E5E9F0]" data-id="element-751">
                    <div className="flex justify-between items-center mb-4" data-id="element-752">
                      <h3 className="text-lg font-bold text-navy-500" data-id="element-753">
                        Medical History
                      </h3>
                      <button type="button" onClick={() => setStep(3)} className="text-sm text-[#0A71B1] font-semibold hover:underline" data-id="element-754">
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-y-2 text-sm" data-id="element-755">
                      <div className="text-gray-500" data-id="element-756">Conditions:</div>
                      <div className="font-medium text-gray-900" data-id="element-757">
                        {formData.conditions.join(', ') || 'None selected'}
                      </div>
                      <div className="text-gray-500" data-id="element-758">Allergies:</div>
                      <div className="font-medium text-gray-900" data-id="element-759">
                        {formData.allergies || 'None listed'}
                      </div>
                    </div>
                  </div>
                </motion.div>}

              {/* Form Navigation */}
              <div className="mt-10 pt-6 border-t border-[#E5E9F0] flex items-center justify-between" data-id="element-760">
                {step > 1 ? <button type="button" onClick={prevStep} className="flex items-center gap-2 px-6 py-3 rounded-[10px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-761">
                    <ChevronLeftIcon className="h-5 w-5" data-id="element-762" /> Back
                  </button> : <div data-id="element-763"></div>}

                {step < 4 ? <button type="submit" className="flex items-center gap-2 bg-[#0A71B1] hover:bg-[#085d91] text-white px-8 py-3 rounded-[10px] font-semibold transition-colors" data-id="element-764">
                    Next <ChevronRightIcon className="h-5 w-5" data-id="element-765" />
                  </button> : <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 bg-[#E8891D] hover:bg-[#d07a15] text-white px-8 py-3 rounded-full font-semibold transition-colors disabled:opacity-70" data-id="element-766">
                    {isSubmitting ? 'Submitting...' : 'Submit Paperwork'}{' '}
                    <CheckCircle2Icon className="h-5 w-5" data-id="element-767" />
                  </button>}
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </>;
}